/*
 * Copyright (c) 2024-2026 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import {HttpClient} from '@angular/common/http';
import {Component, ElementRef, EventEmitter, Output, ViewChild} from '@angular/core';
import {ICellEditorAngularComp} from 'ag-grid-angular';
import {CommonHelperService} from '../../service/common-helper.service';
import {EsciMessageService} from '../../shared/esci-message.service';

export interface AttachmentCellRendererParams {
    urlBase?: string;
    fileIdField?: string;
    nameField?: string | null;
    pendingFileProperty?: string;
    defaultDisplayName?: string;
    componentIdField?: string;
}

@Component({
    selector: 'attachment-editor',
    template: `
        <div class="attachment-content">
        @if ((fileId > 0) || pendingFile) {
          @if ((fileId > 0)) {
            <a class="attachment-link" [title]="displayName || ''"
              [href]="downloadHref || '#'" target="_blank" rel="noopener noreferrer"
              (click)="downloadExistingAttachment($event)">{{displayName}}</a>
          }
          @if (pendingFile) {
            <a class="attachment-link" [title]="displayName || ''"
              [href]="fileUrl" target="_blank" download="{{displayName}}">{{displayName}}</a>
          }
          @if (!readOnly) {
            <button mat-icon-button (click)="removeSelectedFile()"
              class="remove-button" title="Remove Attachment">X
            </button>
          }
        } @else {
          @if (!readOnly) {
            <a title="Upload attachment" (click)="triggerFileInput()"><span class="fa fa-upload"></span></a>
          }
          <input type="file" #fileInput style="display: none;" (change)="onFileSelected($event)">
        }
        </div>
        `,
    styles: [`
    .attachment-content {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      max-width: 100%;
      min-width: 0;
      white-space: nowrap;
    }
    .attachment-link {
      display: inline-block;
      min-width: 0;
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      vertical-align: middle;
    }
    .remove-button {
      margin: -1px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 auto;
      border: 1px solid grey;
      background-color: #FF000040;
      border-radius: 6px;
    }
  `],
    standalone: false
})
export class AttachmentCellRendererComponent implements ICellEditorAngularComp {

    @ViewChild('fileInput') fileInputRef!: ElementRef<HTMLInputElement>;
    public displayName: string | null = null;
    public urlBase: string | null = null;
    public fileId = -1;
    public compId = -1;
    public pendingFile: File | null = null;
    public fileUrl: string | null = null;
    public downloadHref: string | null = null;
    private fileIdField = 'attachmentFileId';
    private nameField: string | null = 'attachmentName';
    private pendingFileProperty = 'attachmentFile';
    private defaultDisplayName = 'Attachment';
    private componentIdField: string | null = null;
    private params: any;
    public readOnly = true;
    @Output() valueChange = new EventEmitter<any>();

    constructor(
        private readonly http: HttpClient,
        private readonly messageService: EsciMessageService
    ) {}

    agInit(params: any): void {
        const rendererParams = params.colDef.cellRendererParams ?? params.colDef.cellEditorParams;
        this.applyRendererParams(rendererParams);
        this.urlBase = rendererParams?.urlBase ?? null;
        const rowId = params.data.id;
        const originalRowId = params.data.originalRowData?.id;
        this.compId = rowId === -1 && originalRowId != null && originalRowId !== -1 ? originalRowId : rowId;
        this.refreshFromRow(params.data);
        this.params = params;
        // Set readOnly property based on the editable function
        if (typeof params.colDef.editable === 'function') {
            this.readOnly = !params.colDef.editable(params);
        } else {
            this.readOnly = params.colDef.editable === false;
        }
    }

    getValue() {
        const value: Record<string, unknown> = {
            fileId: this.fileId,
            [this.pendingFileProperty]: this.pendingFile
        };
        if (this.nameField) {
            value[this.nameField] = this.displayName;
        }
        return value;
    }

    refresh(params: any): boolean {
        const rendererParams = params.colDef.cellRendererParams ?? params.colDef.cellEditorParams;
        this.applyRendererParams(rendererParams);
        this.refreshFromRow(params.data);
        return true; // Signals ag-Grid to refresh cell content
    }

    triggerFileInput(): void {
        // Use ViewChild to safely trigger the file input
        this.fileInputRef.nativeElement.click();
    }

    onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (file) {
            // Revoke the previous object URL before creating a new one
            this.revokeObjectURLOnFileChange();
            // Clear the previous file ID and set the new file
            this.fileId = -2;
            this.pendingFile = file;
            this.displayName = file.name;
            this.fileUrl = URL.createObjectURL(file);

            // a negative -2 is used to indicate a new file (-1 means no file, a positive number means a file exists)
            const newValue = -2;
            CommonHelperService.setDeepValue(this.params.data, this.params.colDef.field, newValue);
            CommonHelperService.setDeepValue(this.params.data, this.pendingFileProperty, file);
            if (this.nameField) {
                CommonHelperService.setDeepValue(this.params.data, this.nameField, this.displayName);
            }
            this.valueChange.emit(newValue);
        }
        this.params.attachmentChanged?.();
    }

    removeSelectedFile(): void {
        // Revoke the previous object URL before creating a new one
        this.revokeObjectURLOnFileChange();
        this.pendingFile = null;
        this.fileUrl = null;
        this.fileId = -1;
        this.displayName = null;

        CommonHelperService.setDeepValue(this.params.data, this.params.colDef.field, -1);
        CommonHelperService.setDeepValue(this.params.data, this.pendingFileProperty, null);
        if (this.nameField) {
            CommonHelperService.setDeepValue(this.params.data, this.nameField, null);
        }
        this.params.attachmentChanged?.();
    }

    // Utility to clean up object URL
    revokeObjectURLOnFileChange(): void {
        if (this.fileUrl) {
            URL.revokeObjectURL(this.fileUrl);
        }
    }

    downloadExistingAttachment(event: MouseEvent): void {
        event.preventDefault();
        if (!this.urlBase || this.compId < 0 || this.fileId < 0) {
            return;
        }
        const url = `${this.urlBase}/${this.compId}/${this.fileId}`;
        this.http.get(url, {responseType: 'blob'}).subscribe({
            next: (blob) => {
                CommonHelperService.downloadFile(blob, this.displayName || this.defaultDisplayName);
            },
            error: () => {
                this.messageService.processError('Unable to open attachment. Please try again.');
            }
        });
    }

    private applyRendererParams(rendererParams: AttachmentCellRendererParams | undefined): void {
        this.fileIdField = rendererParams?.fileIdField ?? 'attachmentFileId';
        this.nameField = rendererParams?.nameField ?? 'attachmentName';
        this.pendingFileProperty = rendererParams?.pendingFileProperty ?? 'attachmentFile';
        this.defaultDisplayName = rendererParams?.defaultDisplayName ?? 'Attachment';
        this.componentIdField = rendererParams?.componentIdField ?? null;
        if (rendererParams && Object.prototype.hasOwnProperty.call(rendererParams, 'nameField')
            && rendererParams.nameField == null) {
            this.nameField = null;
        }
    }

    private refreshFromRow(rowData: Record<string, unknown>): void {
        // If we previously created an object URL for a pending file, revoke it before recalculating state.
        this.revokeObjectURLOnFileChange();
        this.fileUrl = null;
        this.downloadHref = null;

        const rawFileId = CommonHelperService.getDeepValue(rowData, this.fileIdField);
        this.fileId = typeof rawFileId === 'number' ? rawFileId : Number(rawFileId ?? -1);
        this.pendingFile = (CommonHelperService.getDeepValue(rowData, this.pendingFileProperty) as File | null) ?? null;
        if (this.componentIdField && this.componentIdField.trim().length > 0) {
            const candidateCompId = Number(CommonHelperService.getDeepValue(rowData, this.componentIdField));
            if (Number.isFinite(candidateCompId) && candidateCompId > 0) {
                this.compId = candidateCompId;
            }
        }
        if (this.urlBase && this.compId > 0 && this.fileId > 0) {
            this.downloadHref = `${this.urlBase}/${this.compId}/${this.fileId}`;
        }
        if (this.pendingFile) {
            this.fileUrl = URL.createObjectURL(this.pendingFile);
            this.displayName = this.pendingFile.name;
            return;
        }
        const storedName = this.nameField ? CommonHelperService.getDeepValue(rowData, this.nameField) : null;
        if (typeof storedName === 'string' && storedName.trim().length > 0) {
            this.displayName = storedName;
        } else if (this.fileId > 0) {
            this.displayName = String(this.fileId);
        } else {
            this.displayName = null;
        }
    }

    ngOnDestroy(): void {
        this.revokeObjectURLOnFileChange();
        this.fileUrl = null;
    }
}
